/**
 * PricingPage — public pricing page at /pricing.
 *
 * Required for merchant / MOR verification. Communicates the Free vs Pro
 * split, current one-time price, and feature comparison. CTA opens the
 * PaywallModal so the actual checkout flow stays consistent with
 * everywhere else Pro is sold from. Pro is a one-time lifetime unlock —
 * no subscription, no auto-renewal.
 */
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type BillingStatus } from "@/api/client";
import { PaywallModal } from "@/components/PaywallModal";
import { SignInModal } from "@/components/SignInModal";
import { useAuthStore } from "@/store/authStore";

interface FeatureRow {
  label: string;
  free: string | boolean;
  pro: string | boolean;
}

const FEATURES: FeatureRow[] = [
  { label: "One free-kick puzzle every day", free: true, pro: true },
  { label: "Global leaderboard", free: true, pro: true },
  { label: "Daily streak tracking", free: true, pro: true },
  { label: "Country-locked friend leagues", free: false, pro: true },
  { label: "Practice mode (unlimited free-kicks)", free: false, pro: true },
  { label: "Daily puzzle archive", free: false, pro: true },
  { label: "Stats dashboard & accuracy analytics", free: false, pro: true },
  { label: "Streak insurance (1 free pass / month)", free: false, pro: true },
  { label: "World Cup bracket bonus content", free: false, pro: true },
];

function Tick() {
  return (
    <span aria-label="Included" className="text-emerald-300">
      ✓
    </span>
  );
}
function Cross() {
  return (
    <span aria-label="Not included" className="opacity-30">
      —
    </span>
  );
}

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Pro is launching soon — no specific date surfaced publicly. The page
 * shows a "Launching soon" badge instead of an immediate "Upgrade" CTA.
 * Flip `PRO_LAUNCHED` to `true` when Pro is actually shipping and the
 * page reverts to the normal "Upgrade to Pro" checkout flow.
 */
const PRO_LAUNCHED = true;

export function PricingPage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [founder, setFounder] = useState<{ remaining: number; limit: number } | null>(null);
  const preLaunch = !PRO_LAUNCHED;

  // Public endpoint requires auth. We let unauthenticated visitors see the
  // headline price by hard-coding the WC-special / regular split here, then
  // override with the live server value once the user signs in.
  const isWcSpecial =
    status?.activeTier === "wc_special" ||
    (!status && Date.now() < Date.UTC(2026, 6, 20, 0, 0, 0));
  const headlineCents =
    status?.activePriceCents ?? (isWcSpecial ? 1899 : 3699);

  useEffect(() => {
    if (!user?.email) return;
    api.billingStatus().then(setStatus).catch(() => undefined);
  }, [user?.email]);

  useEffect(() => {
    api.founderStatus().then(setFounder).catch(() => undefined);
  }, []);

  return (
    <main className="mx-auto flex min-h-full max-w-3xl flex-col gap-10 p-6 pb-16 text-white">
      <header className="text-center">
        <Link
          to="/"
          className="text-sm uppercase tracking-widest opacity-50 hover:opacity-100"
        >
          ← Curvle
        </Link>
        <h1 className="mt-4 text-4xl font-bold tracking-tight">Pricing</h1>
        <p className="mt-2 text-base opacity-80">
          Curvle is free to play, every day. Curvle Pro unlocks the rest.
        </p>
        {preLaunch && (
          <p className="mt-3 inline-block rounded-full bg-amber-300/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-amber-200 ring-1 ring-amber-300/30">
            Curvle Pro · Launching soon
          </p>
        )}
      </header>

      {founder && founder.remaining > 0 && (
        <section className="rounded-3xl bg-amber-300/10 p-5 text-center ring-1 ring-amber-300/40">
          <p className="text-sm font-semibold text-amber-200">
            {t("founder.card_title", { limit: founder.limit.toLocaleString() })}
          </p>
          <p className="mt-1 text-xs opacity-80">
            {t("founder.card_subtitle", { remaining: founder.remaining.toLocaleString(), limit: founder.limit.toLocaleString() })}
          </p>
          {!user && (
            <button
              type="button"
              className="mt-3 inline-block rounded-full bg-amber-300 px-5 py-2 text-sm font-bold text-emerald-950 transition hover:bg-amber-200"
              onClick={() => setSignInOpen(true)}
            >
              Claim free Pro — Sign in
            </button>
          )}
        </section>
      )}

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Free */}
        <div className="rounded-3xl bg-white/5 p-6 ring-1 ring-white/10">
          <h2 className="text-xl font-semibold">Free</h2>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-bold">$0</span>
            <span className="text-sm opacity-60">/ forever</span>
          </div>
          <p className="mt-2 text-sm opacity-70">
            One puzzle a day. The full daily experience, free.
          </p>
          <Link
            to="/play"
            className="mt-5 inline-block w-full rounded-full bg-white/10 px-5 py-2.5 text-center font-semibold transition hover:bg-white/20"
          >
            Play today&rsquo;s kick
          </Link>
        </div>

        {/* Pro */}
        <div className="relative rounded-3xl bg-amber-300/10 p-6 ring-1 ring-amber-300/40">
          <span className="absolute right-4 top-4 rounded-full bg-amber-300 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-950">
            Pro
          </span>
          <h2 className="text-xl font-semibold">Curvle Pro</h2>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-bold text-amber-300">
              {formatPrice(headlineCents)}
            </span>
            <span className="text-sm opacity-70">one-time · lifetime access</span>
          </div>
          {isWcSpecial && (
            <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-amber-200">
              World Cup launch pricing
            </p>
          )}
          <p className="mt-2 text-sm opacity-80">
            Practice mode, daily archive, friend leagues, stats, and streak
            insurance. Pay once, yours forever. 7-day refund window.
          </p>

          {user?.isPro ? (
            // User is already Pro — show the manage CTA regardless of launch
            // date (post-launch flow is identical to pre-launch founders).
            <button
              type="button"
              className="mt-5 w-full rounded-full bg-amber-300 px-5 py-2.5 font-semibold text-emerald-950 transition hover:bg-amber-200"
              onClick={() => setPaywallOpen(true)}
            >
              Manage purchase
            </button>
          ) : founder && founder.remaining > 0 && !user ? (
            // Founding Member window — the first 5,000 signups get Pro free.
            // Make the free path the hero: claim via sign-in (no payment, no
            // Gumroad). Signed-in non-Pro users fall through to the paid path.
            <button
              type="button"
              className="mt-5 w-full rounded-full bg-amber-300 px-5 py-2.5 font-bold text-emerald-950 transition hover:bg-amber-200"
              onClick={() => setSignInOpen(true)}
            >
              🏆 Claim free Founding Member Pro — Sign in
            </button>
          ) : preLaunch ? (
            // Pre-launch state. Checkout is intentionally not exposed here
            // until Gumroad products are live and verified. Flip PRO_LAUNCHED
            // above once the Pro feature set is shipping.
            <div className="mt-5 flex flex-col items-center gap-1 rounded-2xl bg-amber-300/10 px-4 py-3 text-center ring-1 ring-amber-300/30">
              <span className="text-base font-semibold text-amber-200">
                🚀 Launching soon
              </span>
              <span className="text-[11px] uppercase tracking-wider opacity-70">
                Pay once, yours forever
              </span>
            </div>
          ) : (
            // PRO_LAUNCHED === true → restore the normal "Upgrade to Pro" CTA.
            <button
              type="button"
              className="mt-5 w-full rounded-full bg-amber-300 px-5 py-2.5 font-semibold text-emerald-950 transition hover:bg-amber-200"
              onClick={() => setPaywallOpen(true)}
            >
              Upgrade to Pro
            </button>
          )}
        </div>
      </section>

      {/* Feature comparison */}
      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-widest opacity-60">
          Compare
        </h3>
        <div className="overflow-hidden rounded-2xl ring-1 ring-white/10">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-left">
              <tr>
                <th className="px-4 py-2 font-semibold">Feature</th>
                <th className="px-4 py-2 text-center font-semibold">Free</th>
                <th className="px-4 py-2 text-center font-semibold text-amber-200">Pro</th>
              </tr>
            </thead>
            <tbody>
              {FEATURES.map((f) => (
                <tr key={f.label} className="border-t border-white/5">
                  <td className="px-4 py-2.5">{f.label}</td>
                  <td className="px-4 py-2.5 text-center">
                    {f.free === true ? <Tick /> : f.free === false ? <Cross /> : f.free}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {f.pro === true ? <Tick /> : f.pro === false ? <Cross /> : f.pro}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQ */}
      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-widest opacity-60">
          FAQ
        </h3>
        <dl className="space-y-4 text-sm">
          {preLaunch && (
            <div>
              <dt className="font-semibold">When does Curvle Pro launch?</dt>
              <dd className="mt-1 opacity-80">
                Curvle Pro is launching soon. The base daily kick +
                leaderboard stay free forever. Pro is a one-time $18.99
                payment that unlocks practice mode, daily archive,
                unlimited Friend Leagues, streak insurance, stats, and
                bracket bonus content. Pay once, yours forever.
              </dd>
            </div>
          )}
          <div>
            <dt className="font-semibold">Is Pro a subscription?</dt>
            <dd className="mt-1 opacity-80">
              No. Curvle Pro is a one-time payment for lifetime access. No
              recurring charges, no auto-renew, nothing to cancel. New
              customers can request a refund within 7 days if Pro
              isn&rsquo;t for them; see our{" "}
              <Link to="/refund" className="underline">refund policy</Link>.
            </dd>
          </div>
          <div>
            <dt className="font-semibold">What payment methods are supported?</dt>
            <dd className="mt-1 opacity-80">
              Credit and debit cards globally, plus PayPal, Apple Pay, and
              Google Pay where available. Payments are processed securely by
              Gumroad, our Merchant of Record.
            </dd>
          </div>
          <div>
            <dt className="font-semibold">Are taxes included?</dt>
            <dd className="mt-1 opacity-80">
              Prices shown are exclusive of any applicable VAT, GST, or sales
              tax. The exact total is calculated at checkout based on your
              billing country.
            </dd>
          </div>
        </dl>
      </section>

      <footer className="border-t border-white/10 pt-6 text-center text-xs opacity-50">
        Curvle is operated by Thoughtplanet Advisory Services LLP, India.{" "}
        <Link to="/terms" className="underline">Terms</Link> ·{" "}
        <Link to="/privacy" className="underline">Privacy</Link> ·{" "}
        <Link to="/refund" className="underline">Refund</Link>
      </footer>

      <PaywallModal open={paywallOpen} onClose={() => setPaywallOpen(false)} />
      <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
    </main>
  );
}
